import { useState } from 'react'
import { ArrowRight, Settings, Store } from 'lucide-react'
import type { LoginResponse, OnboardingState } from '@/types'
import { useVendorAuth } from '@/auth/VendorAuthProvider'
import { AuthLayout } from '@/components/AuthLayout'
import { Spinner } from '@/components/ui'
import { api } from '@/lib/api'
import { setStoredVendor, setToken } from '@/lib/session'
import { useAsync } from '@/lib/useAsync'

/**
 * Selector de cuentas (Fase 3, rename de "wizard de onboarding"): un admin con varias
 * tiendas (o alguna sin reclamar) llega acá con una sesión de cuenta. Para cada website:
 * "Entrar a X" (ya reclamada) o "Configurar X" (todavía no). Ambas acciones devuelven una
 * sesión de admin completa — sin re-login, sin otro dominio (host único).
 */
export function Onboarding() {
  const { logout } = useVendorAuth()
  const { data, loading, error } = useAsync<OnboardingState>(() => api.get('/onboarding/state'), [])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionErr, setActionErr] = useState<string | null>(null)

  function enterWith(session: LoginResponse) {
    setToken(session.token)
    setStoredVendor(session.vendor)
    // El VendorAuthProvider recién va a saber de esta sesión en el próximo render (lee
    // localStorage al montar); forzamos la navegación y un reload de contexto simple.
    window.location.assign('/admin')
  }

  async function enter(websiteId: string) {
    setBusyId(websiteId)
    setActionErr(null)
    try {
      enterWith(await api.post<LoginResponse>('/onboarding/select-tenant', { websiteId }))
    } catch (e) {
      setActionErr((e as Error).message)
      setBusyId(null)
    }
  }

  async function configure(websiteId: string) {
    setBusyId(websiteId)
    setActionErr(null)
    try {
      enterWith(await api.post<LoginResponse>('/onboarding/claim', { websiteId }))
    } catch (e) {
      setActionErr((e as Error).message)
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <AuthLayout title="Tus tiendas">
        <Spinner className="mx-auto h-6 w-6" />
      </AuthLayout>
    )
  }

  if (error) {
    return (
      <AuthLayout title="Tus tiendas">
        <p className="text-sm text-danger">{error.message}</p>
        <button className="btn-ghost btn-block mt-4" onClick={logout}>
          Salir
        </button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Elegí una tienda">
      <div className="space-y-2">
        {(data?.websites ?? []).map((w) => (
          <div key={w.websiteId} className="card flex items-center justify-between gap-3 p-3 text-sm">
            <div className="flex items-center gap-2 overflow-hidden">
              <Store className="h-4 w-4 shrink-0 text-ink-faint" />
              <span className="truncate">{w.url}</span>
            </div>
            {w.claimed ? (
              <button
                type="button"
                className="btn-primary shrink-0 px-3 py-1.5 text-xs"
                disabled={busyId === w.websiteId}
                onClick={() => enter(w.websiteId)}
              >
                {busyId === w.websiteId ? (
                  <Spinner className="h-3 w-3" />
                ) : (
                  <>
                    Entrar <ArrowRight className="h-3 w-3" />
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                className="btn-secondary shrink-0 px-3 py-1.5 text-xs"
                disabled={busyId === w.websiteId}
                onClick={() => configure(w.websiteId)}
              >
                {busyId === w.websiteId ? (
                  <Spinner className="h-3 w-3" />
                ) : (
                  <>
                    Configurar <Settings className="h-3 w-3" />
                  </>
                )}
              </button>
            )}
          </div>
        ))}
      </div>

      {actionErr && <div className="mt-4 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{actionErr}</div>}

      <button type="button" className="btn-ghost btn-block mt-4" onClick={logout}>
        Salir
      </button>
    </AuthLayout>
  )
}
